import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import {
  useGetGymPricingPolicy,
  useUpdateGymPricingPolicy,
  useListTrainers,
  useUpdateTrainerAdminSettings,
  getGetGymPricingPolicyQueryKey,
  getListTrainersQueryKey,
  TrainerProfile,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Percent, Save, Pencil, Info, Users, DollarSign } from "lucide-react";

export default function AdminRevenueSplit() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const gymId = user?.gymId ?? "";

  const { data: pricingPolicy, isLoading: policyLoading } = useGetGymPricingPolicy(gymId, {
    query: { enabled: !!gymId },
  });
  const { data: trainers, isLoading: trainersLoading } = useListTrainers(gymId, undefined, {
    query: { enabled: !!gymId },
  });

  const updatePricingPolicy = useUpdateGymPricingPolicy();
  const updateTrainerSettings = useUpdateTrainerAdminSettings();

  const [defaultGymPct, setDefaultGymPct] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<TrainerProfile | null>(null);
  const [trainerGymPct, setTrainerGymPct] = useState("");

  useEffect(() => {
    if (pricingPolicy) {
      setDefaultGymPct(String(pricingPolicy.defaultGymSplitPct));
    }
  }, [pricingPolicy]);

  const handleSaveDefault = async () => {
    const pct = parseFloat(defaultGymPct);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast({ title: "Please enter a valid percentage between 0 and 100", variant: "destructive" });
      return;
    }
    try {
      await updatePricingPolicy.mutateAsync({
        id: gymId,
        data: {
          pricingMode: pricingPolicy?.pricingMode ?? "GYM_DEFAULT",
          defaultGymSplitPct: pct,
          allowPerTrainerSplit: pricingPolicy?.allowPerTrainerSplit ?? true,
        },
      });
      queryClient.invalidateQueries({ queryKey: getGetGymPricingPolicyQueryKey(gymId) });
      toast({ title: "Default split updated successfully" });
    } catch {
      toast({ title: "Failed to update default split", variant: "destructive" });
    }
  };

  const openTrainerEdit = (trainer: TrainerProfile) => {
    setSelectedTrainer(trainer);
    const effectivePct = trainer.gymSplitPct != null ? trainer.gymSplitPct : (pricingPolicy?.defaultGymSplitPct ?? 20);
    setTrainerGymPct(String(effectivePct));
    setEditOpen(true);
  };

  const handleTrainerSave = async () => {
    if (!selectedTrainer) return;
    const pct = parseFloat(trainerGymPct);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast({ title: "Please enter a valid percentage between 0 and 100", variant: "destructive" });
      return;
    }
    try {
      await updateTrainerSettings.mutateAsync({
        id: selectedTrainer.id,
        data: { gymSplitPct: pct },
      });
      queryClient.invalidateQueries({ queryKey: getListTrainersQueryKey(gymId) });
      setEditOpen(false);
      setSelectedTrainer(null);
      toast({ title: `Split updated for ${selectedTrainer.firstName} ${selectedTrainer.lastName}` });
    } catch {
      toast({ title: "Failed to update trainer split", variant: "destructive" });
    }
  };

  const trainerList: TrainerProfile[] = trainers ?? [];
  const defaultPct = pricingPolicy?.defaultGymSplitPct ?? 20;

  const isLoading = policyLoading || trainersLoading;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Revenue Split</h1>
          <p className="text-muted-foreground text-lg">Manage how session revenue is split between your gym and trainers.</p>
        </div>

        <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-blue-300 font-semibold">Platform Fee</p>
            <p className="text-sm text-blue-300/80 mt-1">
              A platform fee is deducted from each session before the revenue split is applied.
              The percentages below apply to the remaining amount after the platform fee is deducted.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <div className="h-40 glass-panel rounded-2xl animate-pulse" />
            <div className="h-64 glass-panel rounded-2xl animate-pulse" />
          </div>
        ) : (
          <>
            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  Trainer Pricing Control
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Allow trainers to adjust private lesson pricing</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      When enabled, trainers can set their own hourly rate and per-client rate overrides (subject to min/max guardrails).
                      When disabled, all sessions use the gym's default hourly rate.
                    </p>
                  </div>
                  <Switch
                    checked={pricingPolicy?.allowTrainerPricing ?? true}
                    onCheckedChange={async (checked) => {
                      try {
                        await updatePricingPolicy.mutateAsync({
                          id: gymId,
                          data: {
                            pricingMode: pricingPolicy?.pricingMode ?? "GYM_DEFAULT",
                            defaultGymSplitPct: pricingPolicy?.defaultGymSplitPct ?? 20,
                            allowPerTrainerSplit: pricingPolicy?.allowPerTrainerSplit ?? true,
                            allowTrainerPricing: checked,
                          },
                        });
                        queryClient.invalidateQueries({ queryKey: getGetGymPricingPolicyQueryKey(gymId) });
                        toast({ title: checked ? "Trainer pricing enabled" : "Trainer pricing disabled" });
                      } catch {
                        toast({ title: "Failed to update setting", variant: "destructive" });
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide flex items-center gap-2">
                  <Percent className="w-5 h-5 text-primary" />
                  Default Split
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-6">
                  This is the default revenue split applied to all new trainers. Individual trainers can be overridden below.
                </p>
                <div className="flex items-end gap-6 flex-wrap">
                  <div className="space-y-2">
                    <Label>Gym Share (%)</Label>
                    <Input
                      className="glass-panel border-white/10 w-32"
                      type="number"
                      min={0}
                      max={100}
                      value={defaultGymPct}
                      onChange={e => setDefaultGymPct(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trainer Share (%)</Label>
                    <Input
                      className="glass-panel border-white/10 w-32 bg-white/[0.02]"
                      type="number"
                      value={defaultGymPct ? String(100 - parseFloat(defaultGymPct || "0")) : ""}
                      disabled
                    />
                  </div>
                  <Button
                    onClick={handleSaveDefault}
                    disabled={updatePricingPolicy.isPending}
                    className="gap-2"
                  >
                    {updatePricingPolicy.isPending ? "Saving..." : <><Save className="w-4 h-4" /> Save Default</>}
                  </Button>
                </div>
                <div className="mt-4 flex gap-6">
                  <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5 flex-1 max-w-[200px]">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Gym Keeps</p>
                    <p className="text-2xl font-mono font-bold text-white">{defaultGymPct || "0"}%</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5 flex-1 max-w-[200px]">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Trainer Keeps</p>
                    <p className="text-2xl font-mono font-bold text-white">{defaultGymPct ? 100 - parseFloat(defaultGymPct || "0") : "0"}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="font-display text-xl uppercase tracking-wide flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Trainer Splits
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trainerList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
                    <Users className="w-10 h-10 text-muted-foreground" />
                    <p className="text-muted-foreground">No trainers yet. Trainers will appear here once they accept an invite.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-xs text-muted-foreground uppercase tracking-wider font-semibold">Trainer</th>
                          <th className="text-center py-3 px-4 text-xs text-muted-foreground uppercase tracking-wider font-semibold">Gym %</th>
                          <th className="text-center py-3 px-4 text-xs text-muted-foreground uppercase tracking-wider font-semibold">Trainer %</th>
                          <th className="text-center py-3 px-4 text-xs text-muted-foreground uppercase tracking-wider font-semibold">Override</th>
                          <th className="text-right py-3 px-4 text-xs text-muted-foreground uppercase tracking-wider font-semibold">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trainerList.map((trainer) => {
                          const gymPct = trainer.gymSplitPct != null ? trainer.gymSplitPct : defaultPct;
                          const trainerPct = 100 - gymPct;
                          const isOverridden = trainer.gymSplitPct != null && trainer.gymSplitPct !== defaultPct;
                          return (
                            <tr key={trainer.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-bold border border-white/10 shrink-0">
                                    {trainer.firstName[0]}{trainer.lastName[0]}
                                  </div>
                                  <div>
                                    <p className="text-white font-semibold text-sm">{trainer.firstName} {trainer.lastName}</p>
                                    <p className="text-xs text-muted-foreground">{trainer.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="font-mono font-bold text-white">{gymPct}%</span>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="font-mono font-bold text-white">{trainerPct}%</span>
                              </td>
                              <td className="text-center py-4 px-4">
                                {isOverridden ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30">Custom</span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/5 text-muted-foreground border border-white/10">Default</span>
                                )}
                              </td>
                              <td className="text-right py-4 px-4">
                                <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => openTrainerEdit(trainer)}>
                                  <Pencil className="w-3.5 h-3.5" /> Edit
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">
              Edit Split — {selectedTrainer?.firstName} {selectedTrainer?.lastName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Set a custom revenue split for this trainer. This overrides the gym's default split ({defaultPct}% gym / {100 - defaultPct}% trainer).
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gym Share (%)</Label>
                <Input
                  className="glass-panel border-white/10"
                  type="number"
                  min={0}
                  max={100}
                  value={trainerGymPct}
                  onChange={e => setTrainerGymPct(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Trainer Share (%)</Label>
                <Input
                  className="glass-panel border-white/10 bg-white/[0.02]"
                  type="number"
                  value={trainerGymPct ? String(100 - parseFloat(trainerGymPct || "0")) : ""}
                  disabled
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleTrainerSave} disabled={updateTrainerSettings.isPending}>
              {updateTrainerSettings.isPending ? "Saving..." : "Save Split"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
